import { createSpaceIconBatcher } from '#pplx/frontend/apps/ask/collections/spaceIconBatcher.js';
import { batchGetSpaceIcons } from '#pplx/frontend/apps/ask/queries/spaces-queries/batchGetSpaceIcons.js';
import { SPACE_ICON_GC_TIME_MS } from '#pplx/frontend/apps/ask/queries/spaces-queries/constants.js';
import { getSpaceIconKey } from '#pplx/frontend/apps/ask/queries/spaces-queries/getSpaceIconKey.js';
import { seedSpaceIcons } from '#pplx/frontend/apps/ask/queries/spaces-queries/seedSpaceIcons.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useQueryClientAccess } from '#pplx/frontend/react-query/useQueryClientAccess.js';
import { spacesClient } from '#pplx_generated/typespec/rest/spaces/client.js';
import { useQueries, useQueryClient } from '@tanstack/react-query';
// One coalesced request serves many concurrent callers, so the batch carries
// a fixed reason instead of per-caller ones.
const SPACE_ICON_BATCH_REASON = 'space-icon-batch';
// One batcher per QueryClient: the batch results seed per-icon entries into
// the cache they serve, and fresh QueryClients (tests) get isolated batchers.
const batchers = new WeakMap();
function getSpaceIconBatcher(queryClient) {
    const existing = batchers.get(queryClient);
    if (existing) {
        return existing;
    }
    const batcher = createSpaceIconBatcher(async (ids) => {
        const { icons } = await batchGetSpaceIcons({ client: spacesClient, reason: SPACE_ICON_BATCH_REASON }, { ids });
        // Seed even ids whose observing query unmounted mid-flight.
        seedSpaceIcons(queryClient, icons);
        return icons;
    });
    batchers.set(queryClient, batcher);
    return batcher;
}
export function getSpaceIconQueryOptions({ id, queryClient, }) {
    return {
        queryKey: getSpaceIconKey(id ?? ''),
        queryFn: () => getSpaceIconBatcher(queryClient).load(id ?? ''),
        enabled: Boolean(id),
        gcTime: SPACE_ICON_GC_TIME_MS,
    };
}
function selectIconObservations(results) {
    return results.map(({ data, isLoading }) => ({ data, isLoading }));
}
export function useSpaceIconQueries(ids) {
    const { queryClient, queryOptions } = useQueryClientAccess();
    return useQueries({
        queries: ids.map((id) => queryOptions(getSpaceIconQueryOptions({ id, queryClient }))),
        combine: selectIconObservations,
    }, queryClient);
}
/**
 * Reads one catalog icon by symbol id; data is the wire entry, rendered
 * directly via `<Icon icon={data} />`, and null while loading and for
 * nullish/unknown ids. Concurrent callers coalesce into a single batch
 * request; list/batch responses push-seed this hook's cache key, so a warm
 * cache answers without any network.
 */
export function useSpaceIconQuery(id) {
    const queryClient = useQueryClient();
    return useQuery(getSpaceIconQueryOptions({ id, queryClient }), queryClient);
}
//# sourceMappingURL=useSpaceIconQuery.js.map